/**
 * CompareFeasibilityWarnings.js — 시나리오 비교 탭 경고 위젯.
 *
 * MP 를 포함한 전 모듈이 이 위젯 하나를 쓴다.
 *   RP/IP/DP — mod.feasibilityCfg 선언만으로 공용 컨테이너가 배치.
 *   MP       — cfg/rowTargets 를 직접 넘겨 차트 다이얼로그와 같은 트리에 둔다.
 *
 * 백엔드(base/feasibility.py)가 2단 AND 게이트로 판정한 scn.feasibility_warnings 를 렌더한다.
 *   게이트 1 — 값이 현실적으로 적용 어려운 등급인가 (예: RP 수송 LT 10일 → 0일)
 *   게이트 2 — 실제 결과 DB 에 그 제약이 binding 이라는 증거가 있는가
 * 증거가 없으면 백엔드가 항목 자체를 만들지 않으므로, 여기서 0건은 곧 "진짜 깨끗"이다.
 *
 * 표는 완전 제네릭 — 열 구성(columns)을 서버가 소유한다. 규칙마다 grain 이 다르기 때문
 * (RP=거점×품목×LT, IP=재고등급×정책, DP=거래처계층×매출). 규칙을 추가해도 이 파일은 안 고친다.
 *   columns: [{ key, label_key, align?, w?, digits?, unit_key?,
 *               i18n?, type?, bands?, emphasis_key?, emphasis_tone? }]
 *     i18n  — 셀 값이 표시 문자열이 아니라 i18n 키(범주형 셀)
 *     type  — "chip"(bands 의 임계 구간으로 문구+색) | "action"(모듈이 셀을 직접 그림)
 *     bands — [{max?, label_key, tone}]. 임계값은 도메인 지식이라 서버가 소유
 *   rows   : columns.key 와 동일한 평면 dict (백엔드 cap 100건, 헤더 건수는 total_count)
 * ⚠️ 서버는 i18n 키만 내려보낸다 — 표시 문자열을 그대로 렌더하지 않는다
 *   (MP warnings[].label 이 프론트에서 무시되는 데드필드가 된 전례를 답습하지 않기 위함).
 *
 * 모듈 훅은 데이터로 표현할 수 없는 것(화면 이동·팝업)에만 쓴다.
 *   rowTargets(row, w)      — 행 드릴다운. 첫 열이 진입점이 된다.
 *   renderCell(row, col, w) — type:"action" 열에서만 호출된다. 어느 열이 모듈 소유인지도
 *                             서버 열 메타가 정하므로, 이 훅으로 값 표시를 빼앗을 수 없다.
 *
 * 렌더 조건: (cfg 직접 주입 또는 mod.feasibilityCfg 보유) + 시나리오 중 하나라도
 *   feasibility_warnings 배열 보유. cfg = { sectionTitleKey } — 섹션 제목 i18n 키.
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Card, MenuItem, Select, Stack, Typography } from "@mui/material";
import { C, NavName, StatusSection } from "./scenarioStatusUi";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { scenarioPickLabel } from "./aiFieldText";

// ── 헬퍼 ──────────────────────────────────────────────────────────────────────

const warnsOf = (sc) => (Array.isArray(sc?.feasibility_warnings) ? sc.feasibility_warnings : []);

// 색조는 백엔드 tone 을 그대로 따른다(구 응답 호환: tone 없으면 severity 로 유도).
//   "info" = 트레이드오프 — 잘못이 아니라 상충 관계라서 경고색 대신 중립(파랑)을 쓴다.
const toneOf = (w) => w?.tone || (w?.severity === "crit" ? "crit" : "warn");

// tone → [글자색, 배경색]. crit=적, warn=주황, info=청(MP statusChip '확인 필요'와 같은 어법).
const TONE_COLORS = {
  crit: [C.g3t, C.g3l, C.g3],
  warn: [C.g2t, C.g2l, C.g2],
  info: [C.accT, C.accL, C.accM],
};
const toneColors = (tone) => TONE_COLORS[tone] || TONE_COLORS.warn;

/**
 * KPI 비교표 '경고' 행 슬롯(mod.scenarioWarnCount)용 공용 헬퍼.
 * 모듈 파일에서 `RP_MODULE.scenarioWarnCount = feasWarnCount;` 한 줄로 배선한다.
 * 공용 컨테이너는 tone(제네릭 심각도)만 받아 색을 정한다 — 모듈별 지식 누출 없음.
 */
export function feasWarnCount(sc) {
  const warns = warnsOf(sc);
  const total = warns.reduce((acc, w) => acc + (Number(w?.total_count) || 0), 0);
  // tone 우선순위 crit > warn > info. info(트레이드오프)만 있으면 경고색을 쓰지 않는다 —
  //   서비스 레벨을 올리든 내리든 어느 쪽도 오류가 아니므로 색으로 실수라고 말하면 안 된다.
  const tone = warns.some((w) => toneOf(w) === "crit") ? "crit"
    : warns.some((w) => toneOf(w) === "warn") ? "warn"
      : (warns.length > 0 ? "info" : "ok");
  return { total, tone };
}

/**
 * 추천 히어로 카드(비교탭 상단) 경고 배지 슬롯(mod.RecHeroBadges)용 공용 컴포넌트.
 * crit=빨강 / warn=주황. 경고 0건이면 아무것도 렌더하지 않는다.
 */
export function FeasibilityHeroBadges({ recSc }) {
  const { t } = useTranslation();
  const warns = warnsOf(recSc);
  if (!warns.length) return null;
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.6} sx={{ mt: 0.7 }}>
      {warns.map((w, i) => {
        const [fg, bg, line] = toneColors(toneOf(w));
        return (
          <Box key={w?.rule_id || i} sx={{
            display: "inline-flex", alignItems: "center", gap: 0.4,
            px: "8px", py: "2px", borderRadius: "6px",
            bgcolor: bg, border: `0.5px solid ${line}`, color: fg,
            fontSize: FONT.CAPTION, fontWeight: 700,
          }}>
            {t(w?.title_key, w?.title_args || {})}
          </Box>
        );
      })}
    </Stack>
  );
}

// 셀 포맷 — 서버 열 메타(digits/unit_key)의 지시만 따르고 포맷 실행은 여기서 한다.
function formatCell(row, col, t) {
  const raw = row?.[col?.key];
  if (raw == null || raw === "") return "—";
  // i18n 열 — 셀 값이 표시 문자열이 아니라 i18n 키다(범주형 셀. 예: 부족 원인).
  if (col?.i18n) return t(String(raw));
  // 기호 단위(%)는 수치에 붙이고, 낱말 단위(시간·일)는 한 칸 띄운다.
  const u = col?.unit_key ? t(col.unit_key) : "";
  const unit = u ? (/^\p{L}/u.test(u) ? ` ${u}` : u) : "";
  if (col?.digits != null && Number.isFinite(Number(raw))) {
    const n = Number(raw).toLocaleString(undefined, {
      minimumFractionDigits: col.digits, maximumFractionDigits: col.digits,
    });
    return `${n}${unit}`;
  }
  return `${raw}${unit}`;
}

// 상태 칩 셀 — 수치를 임계 구간(bands)으로 나눠 문구+색으로 보여준다. 임계값은 백엔드 열 메타가 소유한다.
//   bands 는 max 오름차순, 마지막 항목은 max 없이 "그 이상 전부".
function ChipCell({ row, col, t }) {
  // grid 셀이라 justifySelf 로 정렬한다(텍스트 셀의 textAlign 과 같은 역할).
  const just = col?.align === "right" ? "end" : col?.align === "center" ? "center" : "start";
  const v = Number(row?.[col?.key]);
  if (!Number.isFinite(v)) {
    return <Typography sx={{ fontSize: FONT.SUB, color: C.text3, justifySelf: just }}>—</Typography>;
  }
  const band = (col.bands || []).find((b) => b.max == null || v <= Number(b.max));
  if (!band) {   // 구간을 못 찾으면 빈 칸이 아니라 다른 셀과 같은 "—" 로 둔다.
    return <Typography sx={{ fontSize: FONT.SUB, color: C.text3, justifySelf: just }}>—</Typography>;
  }
  const [fg, bg] = toneColors(band.tone);
  return (
    <Box component="span" sx={{ justifySelf: just, px: "6px", py: "1px", borderRadius: "999px",
      bgcolor: bg, color: fg, fontSize: FONT.MICRO, fontWeight: 700, whiteSpace: "nowrap" }}>
      {t(band.label_key)}
    </Box>
  );
}

// 경고 카드 헤더 — 좌측 심각도 칩 + 중립(먹색) 제목 + 우측 tabular 건수 + CSS 카레트.
//   카드 배경은 흰색, 심각도는 좌측 레일·칩·숫자에만 실어 여러 장이 한 가족으로 스캔되게 한다.
function WarnCardHead({ tone, chipKey, title, count, open, onToggle }) {
  const { t } = useTranslation();
  const [fg, bg] = toneColors(tone);
  const sev = { fg, bg };
  return (
    <Stack direction="row" alignItems="center" gap={1.1}
      sx={{ cursor: "pointer", mb: open ? 0.6 : 0 }} onClick={onToggle}>
      <Box component="span" sx={{ fontSize: FONT.MICRO, fontWeight: 800, letterSpacing: "0.01em",
        px: "8px", py: "2.5px", borderRadius: "999px", bgcolor: sev.bg, color: sev.fg,
        whiteSpace: "nowrap", flexShrink: 0 }}>{t(chipKey)}</Box>
      <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1, letterSpacing: "-0.01em",
        minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</Typography>
      <Typography sx={{ ml: "auto", flexShrink: 0, fontSize: FONT.SUB, fontWeight: 800, color: sev.fg,
        fontVariantNumeric: "tabular-nums" }}>{t("countCases", { count })}</Typography>
      <Box component="span" sx={{ flexShrink: 0, width: 7, height: 7,
        borderRight: `2px solid ${C.text3}`, borderBottom: `2px solid ${C.text3}`,
        transform: open ? "rotate(45deg)" : "rotate(-45deg)", transition: "transform 0.18s", opacity: 0.75 }} />
    </Stack>
  );
}

// 0건 행 — 빈 화면을 '오류/미실행'으로 오해하지 않도록 '정상 · 위반 없음'을 적극 확인한다.
function WarnCleanRow({ title }) {
  const { t } = useTranslation();
  return (
    <Card variant="outlined" sx={{ borderColor: `${C.g1}33`, borderLeft: `4px solid ${C.g1s}`,
      borderRadius: "13px", bgcolor: C.bg2 }}>
      <Box sx={{ p: "11px 15px" }}>
        <Stack direction="row" alignItems="center" gap={1.1}>
          <Box component="span" sx={{ fontSize: FONT.MICRO, fontWeight: 800, px: "8px", py: "2.5px",
            borderRadius: "999px", bgcolor: C.g1l, color: C.g1t, flexShrink: 0 }}>{t("normalBadge")}</Box>
          <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 600, color: C.text2,
            minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</Typography>
          <Typography sx={{ ml: "auto", flexShrink: 0, fontSize: FONT.CAPTION, fontWeight: 700,
            color: C.g1t }}>{t("feasNoViolation")}</Typography>
        </Stack>
      </Box>
    </Card>
  );
}

// 제네릭 증거 표 — columns 메타로 grid 트랙을 만들고 rows 를 그대로 그린다.
//   백엔드가 최대 EVIDENCE_CAP(100)행을 내려보내므로 세로 스크롤이 필수.
const EVIDENCE_MAX_H = 300;

// rowTargets 를 주면 첫 열이 드릴다운 진입점이 된다(2개 이상이면 NavName 이 선택 메뉴를 연다).
// renderCell 은 데이터로 표현할 수 없는 셀(조작 버튼 등)을 모듈이 직접 그리게 하는 탈출구.
function EvidenceTable({ columns, rows, rowTargets, renderCell }) {
  const { t } = useTranslation();
  const cols = Array.isArray(columns) ? columns : [];
  if (!cols.length || !rows?.length) return null;
  // 좌측 정렬 열만 넓게 — 명칭·코드가 들어가기 때문. 수치/칩 열은 좁게 잡는다.
  //   w(폭 가중치)로 열별 조정 가능 — 명칭이 긴 열에 준다.
  const gridCol = cols.map((c) => (c?.align === "left" || !c?.align
    ? `minmax(110px, ${c?.w || 1.3}fr)` : `minmax(96px, ${c?.w || 1}fr)`)).join(" ");
  return (
    // 가로·세로 스크롤은 한 컨테이너가 맡는다 — 헤더를 밖에 두면 본문만 스크롤바 폭만큼 좁아져 열이 어긋난다.
    <Box sx={{ maxHeight: EVIDENCE_MAX_H, overflow: "auto" }}>
      <Box sx={{ minWidth: `${cols.length * 110}px` }}>
        {/* 헤더 — CAPTION(11) 크기. sticky 라 불투명 배경(C.bg2)이 필요하다.
            정렬은 열 지정과 무관하게 항상 가운데 — 헤더 줄이 고르게 읽히도록. */}
        <Box sx={{ display: "grid", gridTemplateColumns: gridCol, gap: "8px",
          px: "10px", py: "6px", borderBottom: `1px solid ${C.border}`,
          position: "sticky", top: 0, zIndex: 1, bgcolor: C.bg2 }}>
          {cols.map((c, ci) => (
            <Typography key={ci} sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text3,
              textAlign: "center", whiteSpace: "nowrap" }}>
              {t(c.label_key)}
            </Typography>
          ))}
        </Box>
        {/* 본문 */}
        {rows.map((row, ri) => (
          <Box key={ri} sx={{ display: "grid", gridTemplateColumns: gridCol, gap: "8px",
            px: "10px", py: "7px",
            borderBottom: ri === rows.length - 1 ? "none" : `0.5px solid ${C.border}`,
            "&:hover": { bgcolor: C.bg } }}>
            {cols.map((c, ci) => {
              // type:"action" 인 열에서만 모듈에 렌더를 넘긴다 — 어느 열이 모듈 소유인지도 서버 열 메타가 정한다.
              if (c?.type === "action") {
                return <React.Fragment key={ci}>{renderCell?.(row, c)}</React.Fragment>;
              }
              if (c?.type === "chip") return <ChipCell key={ci} row={row} col={c} t={t} />;
              if (ci === 0 && rowTargets) {
                return (
                  <NavName key={ci} targets={rowTargets(row)} sx={{ fontSize: FONT.SUB, fontWeight: 600 }}>
                    {formatCell(row, c, t)}
                  </NavName>
                );
              }
              // emphasis_key — 그 행의 값이 참이면 emphasis_tone 색으로 강조한다(색도 서버가 정한다).
              const emph = c?.emphasis_key && row?.[c.emphasis_key];
              return (
                <Typography key={ci} sx={{ fontSize: FONT.SUB,
                  fontWeight: emph ? 800 : 600,
                  color: emph ? toneColors(c.emphasis_tone)[0] : C.text1,
                  textAlign: c?.align || "left",
                  fontVariantNumeric: "tabular-nums",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {formatCell(row, c, t)}
                </Typography>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── 본체 ──────────────────────────────────────────────────────────────────────

// chip_key 미지정 시 tone 에서 유도.
const chipKeyFor = (tone) => (tone === "crit" ? "feasChipInfeasible"
  : tone === "info" ? "feasChipTradeoff" : "feasChipCaution");

export default function CompareFeasibilityWarnings({ mod, cfg, rowTargets, renderCell }) {
  const { t } = useTranslation();
  const scenarios = Array.isArray(mod?.scenarios) ? mod.scenarios : [];
  // 펼침 상태는 rule_id 기준이라 시나리오를 바꿔도 유지된다(같은 항목의 시나리오 간 비교가 매끄럽다).
  const [selIdx, setSelIdx] = useState(mod?.aiRec ?? 0);
  const [expanded, setExpanded] = useState({});
  const toggleCard = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  // cfg 직접 주입 — 모듈이 이 위젯을 스스로 배치할 때 쓴다(MP 는 차트 다이얼로그와 같은 트리에 둔다).
  const conf = cfg || mod?.feasibilityCfg;
  // 백엔드가 아직 이 모듈에 경고를 방출하지 않으면(구 결과 포함) 섹션 자체를 렌더하지 않는다.
  //   빈 배열은 "판정 결과 0건"이므로 clean row 로 표시 — 키 부재와 구분한다.
  const hasFeed = scenarios.some((s) => Array.isArray(s?.feasibility_warnings));
  if (!conf || !hasFeed) return null;

  const title = t(conf.sectionTitleKey || "feasWarnSectionTitle");
  const sc = scenarios[Math.min(selIdx, Math.max(scenarios.length - 1, 0))];
  const warns = warnsOf(sc);
  const total = warns.reduce((acc, w) => acc + (Number(w?.total_count) || 0), 0);

  const scenarioPicker = scenarios.length > 1 ? (
    <Select size="small" value={selIdx} onChange={(e) => setSelIdx(Number(e.target.value))}
      sx={{ minWidth: 140, "& .MuiSelect-select": { py: 0.4, fontSize: FONT.SUB, fontWeight: 600 } }}>
      {scenarios.map((s, i) => (
        <MenuItem key={s?.role ?? i} value={i} sx={{ fontSize: FONT.SUB }}>
          {scenarioPickLabel(s, i, t)}
        </MenuItem>
      ))}
    </Select>
  ) : null;

  return (
    <StatusSection
      title={title}
      subtitle={total > 0 ? t("countCases", { count: total }) : undefined}
      right={scenarioPicker}
      cardSx={{ mt: 1.5 }}
    >
      <Stack spacing={1.1}>
        {/* 0건이면 섹션 제목을 그대로 쓴다 — 별도 키를 두면 두 문구가 갈라진다. */}
        {warns.length === 0 && <WarnCleanRow title={title} />}
        {warns.map((w, i) => {
          const key = w?.rule_id || w?.type || String(i);
          const open = Boolean(expanded[key]);
          const tone = toneOf(w);
          const rail = toneColors(tone)[2];
          return (
            <Card key={key} variant="outlined" sx={{
              borderColor: `${rail}33`,
              borderLeft: `4px solid ${rail}`,
              borderRadius: "13px", bgcolor: C.bg2,
            }}>
              <Box sx={{ p: "11px 15px" }}>
                <WarnCardHead
                  tone={tone}
                  chipKey={w?.chip_key || chipKeyFor(tone)}
                  title={t(w?.title_key, w?.title_args || {})}
                  count={Number(w?.total_count) || 0}
                  open={open}
                  onToggle={() => toggleCard(key)}
                />
                {open && (
                  <Box sx={{ mt: 1 }}>
                    {/* 요약 지표·안내는 설명 문장이므로 본문 크기(BODY 12px) — 칩/배지용 11px 이하는 읽기 부담. */}
                    {w?.summary?.metric_key && (
                      <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text2, mb: 0.8 }}>
                        {t(w.summary.metric_key)} ·{" "}
                        {Number(w.summary.value).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                        {w.summary.unit_key ? ` ${t(w.summary.unit_key)}` : ""}
                      </Typography>
                    )}
                    {/* rowTargets 는 경고(w)도 받는다 — 카드마다 이동할 화면이 다르다. */}
                    <EvidenceTable columns={w?.columns} rows={w?.rows}
                      rowTargets={rowTargets && ((row) => rowTargets(row, w))}
                      renderCell={renderCell && ((row, col) => renderCell(row, col, w))} />
                    {Number(w?.total_count) > (w?.rows?.length || 0) && (
                      <Typography sx={{ mt: 0.7, fontSize: FONT.BODY, color: C.text3 }}>
                        {t("feasRowsTruncated", { shown: w?.rows?.length || 0, total: Number(w?.total_count) || 0 })}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Card>
          );
        })}
      </Stack>
    </StatusSection>
  );
}
